import React, { useState, useCallback, useRef, useMemo } from "react";
import { ReactController } from "../react_controller";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReadOnlyPrimerGrid } from "../../components/grids";
import { EventCard } from "../../components/events/EventCard";
import { CreateEventCard } from "../../components/events/CreateEventCard";
import { CalendarIcon } from "@primer/octicons-react";
import { readStoredViewMode, storeViewMode } from "../../components/grids/persistedViewMode";

// One key for every events index (org dashboard and admin), so the choice
// follows the operator between them.
const VIEW_MODE_KEY = "events";

const queryClient = new QueryClient();

function EventsGrid(props) {
  // GR-3342: read the remembered view first, so the reload that follows a
  // bulk archive does not drop the operator from table back to cards.
  const [viewMode, setViewMode] = useState(() => readStoredViewMode(VIEW_MODE_KEY, props.defaultViewMode || "card"));
  const handleViewModeChange = useCallback((mode) => {
    storeViewMode(VIEW_MODE_KEY, mode);
    setViewMode(mode);
  }, []);
  const searchTimeout = useRef(null);

  const renderCard = (row, columns) => <EventCard row={row} columns={columns} />;

  // GR-1695: a leading "create event" card in card view, when the server says
  // the user may create events (newEventUrl present).
  // GR-4212: someone who can only request events gets a request card instead.
  // The grid shows its leading card in place of the empty-state blankslate, so
  // a requester with no events sees the way in first.
  let leadingCard;
  if (props.newEventUrl) {
    leadingCard = <CreateEventCard href={props.newEventUrl} subtitle="Set up a new event" />;
  } else if (props.requestEventUrl) {
    leadingCard = (
      <CreateEventCard href={props.requestEventUrl} label={props.requestEventLabel} />
    );
  }

  const [searchQuery, setSearchQuery] = useState("");
  const handleSearch = useCallback((query) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearchQuery(query), 300);
  }, []);

  const withSearch = (url) => {
    if (!url || !searchQuery) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}search=${encodeURIComponent(searchQuery)}`;
  };

  const resultsUrl = withSearch(props.resultsUrl);
  // GR-3342: a select-all archives every row matching what the grid is
  // showing, and the server resolves that set itself, so the search has to
  // reach the archive endpoint exactly as it reaches the results one. The tab
  // rides along on the URL the server rendered.
  const bulkArchiveUrl = withSearch(props.bulkArchiveUrl);

  const toolbarActions = useMemo(() => {
    const actions = [];
    if (props.calendarUrl) {
      actions.push({
        icon: CalendarIcon,
        label: "Add to Calendar",
        onClick: props.calendarUrl, // ToolbarAction accepts a URL string for link-like actions
      });
    }
    return actions;
  }, [props.calendarUrl]);

  return (
    <QueryClientProvider client={queryClient}>
      <ReadOnlyPrimerGrid
        {...props}
        resultsUrl={resultsUrl}
        bulkArchiveUrl={bulkArchiveUrl}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        renderCard={renderCard}
        leadingCard={leadingCard}
        onSearch={handleSearch}
        searchPlaceholder="Search Events"
        toolbarActions={toolbarActions}
      />
    </QueryClientProvider>
  );
}

export class EventsGridController extends ReactController {
  connect() {
    this.root.render(<EventsGrid {...this.propsValue} />);
  }
}
